import { Controller } from "@hotwired/stimulus"

// Progressive enhancement over the crawlable pagination rendered by
// Pagination::LoadMoreComponent. The "Load More" link is a real <a> to the
// next /page-N URL; this controller hijacks it to fetch that page and append
// its grid items in place, and an IntersectionObserver triggers it
// automatically as the user nears the bottom (bounded by autoLoad so the
// footer stays reachable — after that, loading takes a click). If anything
// fails, the link still works as plain navigation, which is also all crawlers
// and no-JS visitors ever see.
//
// It also runs in reverse: land deep (e.g. /tags/nature/page-4), scroll back
// up, and reaching the top of the grid prepends the previous page's items and
// winds the address bar down toward the bare URL — page-4 → page-3 → … → base.
// This is driven off the crawlable "‹ Previous" link, so no-JS visitors keep
// the same backward path as a plain click.
export default class extends Controller {
  static targets = ["link", "status", "prevLink"]
  static values = {
    gridSelector: { type: String, default: "[data-card-grid]" },
    autoLoad: { type: Number, default: 3 }
  }

  connect() {
    this.loading = false
    this.autoLoadsLeft = this.autoLoadValue
    if (this.hasLinkTarget && "IntersectionObserver" in window) {
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.autoLoad()
      }, { rootMargin: "600px 0px" })
      this.observer.observe(this.element)
    }

    // Reverse pagination: capture the current page's "previous" URL once, then
    // walk it backward one page per trip to the top. Tracked in JS (not re-read
    // from the DOM) because a forward load rewrites the footer's status line.
    this.prevUrl = this.hasPrevLinkTarget ? this.prevLinkTarget.getAttribute("href") : null
    if (this.prevUrl && "IntersectionObserver" in window) this.observeTop()
  }

  disconnect() {
    this.observer?.disconnect()
    this.topObserver?.disconnect()
  }

  autoLoad() {
    if (this.autoLoadsLeft < 1) return
    this.autoLoadsLeft--
    this.load()
  }

  load(event) {
    event?.preventDefault()
    if (this.loading || !this.hasLinkTarget) return
    this.loading = true
    const url = this.linkTarget.href
    this.element.setAttribute("aria-busy", "true")

    fetch(url, { headers: { Accept: "text/html" } })
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.text()
      })
      .then(html => this.appendPage(html, url))
      .catch(() => { if (event) window.location.assign(url) })
      .finally(() => {
        this.loading = false
        this.element.removeAttribute("aria-busy")
      })
  }

  appendPage(html, url) {
    const doc = new DOMParser().parseFromString(html, "text/html")
    const grid = document.querySelector(this.gridSelectorValue)
    const fetchedGrid = doc.querySelector(this.gridSelectorValue)
    if (!grid || !fetchedGrid) {
      window.location.assign(url)
      return
    }

    // The masonry sizer is layout plumbing, not an item — never duplicate it.
    const items = [...fetchedGrid.children].filter(el => !el.hasAttribute("data-masonry-sizer"))
    grid.append(...items)
    // Let the masonry layout (when this grid has one) absorb the new items.
    grid.dispatchEvent(new CustomEvent("load-more:appended", { detail: { items } }))

    // Reflect the deepest loaded page in the address bar (keeping Turbo's
    // history state intact) so reload/share lands near where the user was.
    history.replaceState(history.state, "", url)

    this.advance(doc)
  }

  // Adopt the fetched footer's next link and position line. On the last page
  // there is no next link, so the button goes away and auto-loading stops.
  advance(doc) {
    const fetchedLink = doc.querySelector("[data-load-more-target='link']")
    if (fetchedLink) {
      this.linkTarget.href = fetchedLink.getAttribute("href")
    } else {
      this.observer?.disconnect()
      this.linkTarget.remove()
    }

    const fetchedStatus = doc.querySelector("[data-load-more-target='status']")
    if (fetchedStatus && this.hasStatusTarget) this.statusTarget.innerHTML = fetchedStatus.innerHTML
  }

  // Watch a sentinel at the top of the grid. It's in view on landing (the user
  // starts at the top), so we don't wind backward until they've scrolled the
  // sentinel out of view once — i.e. a deliberate return to the top, not the
  // initial page load, is what triggers a prepend.
  observeTop() {
    const grid = document.querySelector(this.gridSelectorValue)
    if (!grid || !grid.parentNode) return

    this.topSentinel = document.createElement("div")
    this.topSentinel.setAttribute("data-load-more-sentinel", "")
    this.topSentinel.setAttribute("aria-hidden", "true")
    // A non-zero box so the observer reliably reports intersection everywhere.
    this.topSentinel.style.height = "1px"
    grid.parentNode.insertBefore(this.topSentinel, grid)

    this.armed = false
    this.topObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting) this.armed = true
        else if (this.armed) this.loadPrev()
      }
    }, { rootMargin: "600px 0px" })
    this.topObserver.observe(this.topSentinel)
  }

  loadPrev() {
    if (this.loading || !this.prevUrl) return
    this.loading = true
    const url = this.prevUrl
    this.element.setAttribute("aria-busy", "true")

    fetch(url, { headers: { Accept: "text/html" } })
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.text()
      })
      .then(html => this.prependPage(html, url))
      // A failed reverse fetch just leaves the page as-is — unlike a click,
      // scrolling shouldn't yank the user off to a full navigation.
      .catch(() => {})
      .finally(() => {
        this.loading = false
        this.element.removeAttribute("aria-busy")
      })
  }

  prependPage(html, url) {
    const doc = new DOMParser().parseFromString(html, "text/html")
    const grid = document.querySelector(this.gridSelectorValue)
    const fetchedGrid = doc.querySelector(this.gridSelectorValue)
    if (!grid || !fetchedGrid) return

    const items = [...fetchedGrid.children].filter(el => !el.hasAttribute("data-masonry-sizer"))

    // Anchor on the current first item so its viewport position is unchanged
    // after inserting content above it — no visible jump as the page grows
    // upward. Measured across the layout the prepend triggers.
    const anchor = grid.querySelector("[data-masonry-item]") || grid.firstElementChild
    const beforeTop = anchor ? anchor.getBoundingClientRect().top : 0

    // Insert after the masonry sizer (when present) so the new page sorts ahead
    // of the existing items in DOM — and therefore in layout — order.
    const sizer = grid.querySelector("[data-masonry-sizer]")
    if (sizer) sizer.after(...items)
    else grid.prepend(...items)
    grid.dispatchEvent(new CustomEvent("load-more:prepended", { detail: { items } }))

    const afterTop = anchor ? anchor.getBoundingClientRect().top : 0
    window.scrollBy(0, afterTop - beforeTop)

    // Wind the address bar down toward the bare URL as earlier pages appear.
    history.replaceState(history.state, "", url)

    // Chain to the page before this one; when it has no "previous" link we've
    // reached page 1, so stop watching the top.
    const fetchedPrev = doc.querySelector("[data-load-more-target='prevLink']")
    this.prevUrl = fetchedPrev ? fetchedPrev.getAttribute("href") : null
    if (!this.prevUrl) this.topObserver?.disconnect()
  }
}
